import * as THREE from "three";
import type {
  ChatMessageLike,
  EntityLike,
  PlayerLike,
} from "./types";
import { createClient, joinRoom, type GameRoom } from "./network";
import { EntityRenderer } from "./renderer";
import { InputController, type InputState } from "./input";
import { setupHotReload } from "./hotreload";
import { generateSlug, validateSlug } from "./slug";
import { loadConfig } from "./config";
import { requestPasswordIfNeeded } from "./authPrompt";
import { clearStoredToken } from "./auth";
import { parseVibeCommand, readStoredToken, sendVibePrompt, showToast } from "./vibeCommand";

const PLAYER_LERP_MS = 100;
const CHAT_LIMIT = 10;

interface RemotePlayer {
  mesh: THREE.Object3D;
  targetPosition: THREE.Vector3;
  targetRotation: THREE.Euler;
  prevPosition: THREE.Vector3;
  prevRotation: THREE.Euler;
  lerpStart: number;
  lerpDuration: number;
}

bootstrap().catch((err) => {
  console.error("[main] fatal:", err);
  setStatus(`fatal: ${err instanceof Error ? err.message : String(err)}`);
});

// If the user manually edits the #room slug in the URL bar, treat it as a
// room switch rather than silently staying in the current Colyseus room.
// joinRoom only runs at bootstrap, so the simplest correct thing is a reload.
let lastHash = location.hash;
window.addEventListener("hashchange", () => {
  if (location.hash !== lastHash) {
    lastHash = location.hash;
    location.reload();
  }
});

async function bootstrap(): Promise<void> {
  const canvas = mustGetElement<HTMLCanvasElement>("#canvas");
  mustGetElement<HTMLDivElement>("#status"); // ensures #status exists for setStatus()
  const chatOverlay = mustGetElement<HTMLDivElement>("#chat-overlay");
  const chatInput = mustGetElement<HTMLInputElement>("#chat-input");

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setClearColor(0x101015);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x101015);
  scene.fog = new THREE.Fog(0x101015, 30, 120);

  // Default lighting so we can see something before the server attaches anything.
  const ambient = new THREE.AmbientLight(0xffffff, 0.4);
  ambient.name = "engine:ambient";
  scene.add(ambient);
  const hemi = new THREE.HemisphereLight(0xb0c4de, 0x202020, 0.5);
  hemi.name = "engine:hemi";
  scene.add(hemi);

  const camera = new THREE.PerspectiveCamera(72, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 1.7, 5);

  // Ground plane so we have a horizon reference even before any entities arrive.
  const ground = new THREE.Mesh(
    new THREE.PlaneGeometry(200, 200),
    new THREE.MeshStandardMaterial({ color: 0x2a2a35 }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.name = "engine:ground";
  scene.add(ground);

  window.addEventListener("resize", () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });

  const entityRenderer = new EntityRenderer(scene);

  const remotePlayers = new Map<string, RemotePlayer>();

  // Start the render loop immediately so the user sees the world while the
  // client retries connection (rather than a black canvas).
  let pendingMoveSend: ((state: InputState) => void) | null = null;
  const input = new InputController({
    camera,
    canvas,
    chatInput,
    onMove: (state: InputState) => {
      pendingMoveSend?.(state);
    },
  });

  const clock = new THREE.Clock();
  function frame(): void {
    const dt = Math.min(0.1, clock.getDelta());
    input.update(dt);
    tweenRemotePlayers(remotePlayers);
    renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  const roomId = resolveRoomId();
  setStatus(`loading config for ${roomId}...`);

  // Resolve runtime config (Pages -> /vibe-config.json; dev -> localhost).
  const cfg = await loadConfig();
  console.log(`[main] config: server=${cfg.serverUrl} agent=${cfg.agentUrl} auth=${cfg.authUrl}`);

  // Block on the password modal BEFORE attempting to connect. If the user
  // doesn't auth, joinRoom would just spin on Colyseus onAuth rejections
  // forever — gating up-front keeps the UX honest.
  setStatus("sign in to continue");
  const authToken = await requestPasswordIfNeeded({ authUrl: cfg.authUrl });

  setStatus(`connecting to ${roomId}...`);

  const client = await createClient();
  const room: GameRoom = await joinRoom(client, roomId, {
    onStatus: setStatus,
    authToken,
  });

  // If the engine ever 401s us mid-session, clear the cached token so the
  // next reload re-prompts instead of stuck-retrying with a dead token.
  room.onError((code, message) => {
    if (message && /auth/i.test(message)) {
      console.warn("[main] room error mentions auth; clearing stored token");
      clearStoredToken();
    }
  });

  setStatus(`connected: ${roomId} (you=${room.sessionId})`);
  (window as unknown as { __vibeRoom: GameRoom }).__vibeRoom = room;
  pendingMoveSend = (state) => room.send("move", state);

  wireChat(chatInput, async (text) => {
    if (/^\/help\b/i.test(text.trim())) {
      showToast(
        "commands:\n  /vibe <prompt>  — ask the agent to edit this room\n  /build, /spawn, /make — aliases for /vibe\n  /help — this list\n\nanything without a slash is regular chat (visible to others in this room)",
        "info",
        9000,
      );
      return;
    }
    const cmd = parseVibeCommand(text);
    if (cmd.isVibe) {
      if (!cmd.prompt) {
        showToast("usage: /vibe <prompt>  (also: /build, /spawn, /make, /help)", "error", 4000);
        return;
      }
      // Broadcast the prompt so others see what was asked; the world change
      // arrives separately via hot-reload broadcast.
      room.send("chat", { text });
      const toast = showToast(`agent thinking… "${cmd.prompt.slice(0, 60)}${cmd.prompt.length > 60 ? "…" : ""}"`, "info", 0);
      const result = await sendVibePrompt({
        agentUrl: cfg.agentUrl,
        roomSlug: roomId,
        playerId: room.sessionId,
        prompt: cmd.prompt,
        recentChat: Array.from(room.state.chat).slice(-10),
        stateSnapshot: {
          entities: mapKeys(room.state.entities),
          players: mapKeys(room.state.players),
        },
        token: readStoredToken(),
      });
      if (result.ok) {
        toast.update(`✓ ${result.summary}`, "ok", 8000);
      } else {
        toast.update(`agent error: ${result.error}`, "error", 10000);
      }
      return;
    }
    // Unknown slash command — hint the user instead of broadcasting "/foo" as
    // chat (which would just confuse the room).
    if (text.trim().startsWith("/")) {
      showToast(`unknown command: ${text.split(/\s/)[0]} — try /help`, "error", 4000);
      return;
    }
    room.send("chat", { text });
  });

  wireStateCallbacks(room, {
    mySessionId: room.sessionId,
    entityRenderer,
    camera,
    input,
    remotePlayers,
    scene,
    chatOverlay,
  });

  wireServerMessages(room);

  const hotReload = setupHotReload(
    room,
    {
      scene,
      camera,
      renderer,
      state: room.state,
      sendMessage: (type, payload) => room.send(type, payload),
      log: (msg) => console.log(`[hotreload] ${msg}`),
    },
    {
      entityRenderer,
      moduleUrlFor: (id, sha) => {
        // The per-room client module is served by the game-server, not the
        // Pages origin. In prod the Pages bundle is on pages.dev but the
        // .mjs lives on the trycloudflare WS tunnel. Convert wss:// → https://
        // (ws:// → http://) and prefix. Token-gate via ?token= because
        // dynamic import() can't attach Authorization headers cross-origin.
        const base = cfg.serverUrl.replace(/^ws(s?):/, "http$1:");
        const url = `${base}/rooms/${encodeURIComponent(id)}/client.${encodeURIComponent(sha)}.mjs`;
        const tok = readStoredToken();
        return tok ? `${url}?token=${encodeURIComponent(tok)}` : url;
      },
    },
  );
  (window as unknown as { __vibeHotReload: typeof hotReload }).__vibeHotReload = hotReload;
  (window as unknown as { __vibeDebug: unknown }).__vibeDebug = {
    scene,
    camera,
    renderer,
    entityRenderer,
    input,
  };
}

interface StateWiringDeps {
  mySessionId: string;
  entityRenderer: EntityRenderer;
  camera: THREE.PerspectiveCamera;
  input: InputController;
  remotePlayers: Map<string, RemotePlayer>;
  scene: THREE.Scene;
  chatOverlay: HTMLDivElement;
}

function wireStateCallbacks(room: GameRoom, deps: StateWiringDeps): void {
  const state = room.state;

  state.players.onAdd((player: PlayerLike, sessionId: string) => {
    if (sessionId === deps.mySessionId) {
      // First-person: snap camera to spawn pose.
      deps.input.setPose(player.position, player.rotation);
      return;
    }
    const mesh = createAvatarMesh(player);
    mesh.name = `player:${sessionId}`;
    mesh.position.set(player.position.x, player.position.y, player.position.z);
    mesh.rotation.set(player.rotation.x, player.rotation.y, player.rotation.z);
    deps.scene.add(mesh);

    const rp: RemotePlayer = {
      mesh,
      targetPosition: new THREE.Vector3(player.position.x, player.position.y, player.position.z),
      targetRotation: new THREE.Euler(player.rotation.x, player.rotation.y, player.rotation.z, "YXZ"),
      prevPosition: new THREE.Vector3(player.position.x, player.position.y, player.position.z),
      prevRotation: new THREE.Euler(player.rotation.x, player.rotation.y, player.rotation.z, "YXZ"),
      lerpStart: performance.now(),
      lerpDuration: 0,
    };
    deps.remotePlayers.set(sessionId, rp);

    if (player.onChange) {
      player.onChange(() => {
        const remote = deps.remotePlayers.get(sessionId);
        if (!remote) return;
        remote.prevPosition.copy(remote.mesh.position);
        remote.prevRotation.copy(remote.mesh.rotation);
        remote.targetPosition.set(player.position.x, player.position.y, player.position.z);
        remote.targetRotation.set(player.rotation.x, player.rotation.y, player.rotation.z, "YXZ");
        remote.lerpStart = performance.now();
        remote.lerpDuration = PLAYER_LERP_MS;
      });
    }
  });

  state.players.onRemove((_player: PlayerLike, sessionId: string) => {
    if (sessionId === deps.mySessionId) return;
    const remote = deps.remotePlayers.get(sessionId);
    if (!remote) return;
    deps.scene.remove(remote.mesh);
    disposeMesh(remote.mesh);
    deps.remotePlayers.delete(sessionId);
  });

  state.entities.onAdd((entity: EntityLike, _id: string) => {
    deps.entityRenderer.addEntity(entity);
    if (entity.onChange) {
      entity.onChange(() => deps.entityRenderer.updateEntity(entity));
    }
  });

  state.entities.onRemove((_entity: EntityLike, id: string) => {
    deps.entityRenderer.removeEntity(id);
  });

  state.chat.onAdd((_msg: ChatMessageLike, _idx: number) => {
    renderChat(deps.chatOverlay, state.chat);
  });
  state.chat.onRemove((_msg: ChatMessageLike, _idx: number) => {
    renderChat(deps.chatOverlay, state.chat);
  });
}

function wireServerMessages(room: GameRoom): void {
  room.onMessage("system", (data: { text: string }) => {
    console.log(`[system] ${data.text}`);
  });
  room.onMessage("agent-thinking", (data: { prompt: string; playerId: string }) => {
    console.log(`[agent] thinking on "${data.prompt}" for ${data.playerId}`);
  });
  room.onMessage("agent-done", (data: { sha: string; summary: string }) => {
    console.log(`[agent] done sha=${data.sha} summary=${data.summary}`);
  });
  room.onLeave((code) => {
    setStatus(`disconnected (code=${code}) — reload to reconnect`);
  });
  room.onError((code, message) => {
    console.warn(`[room] error code=${code} msg=${message}`);
  });
}

function tweenRemotePlayers(players: Map<string, RemotePlayer>): void {
  const now = performance.now();
  for (const rp of players.values()) {
    if (rp.lerpDuration <= 0) {
      rp.mesh.position.copy(rp.targetPosition);
      rp.mesh.rotation.copy(rp.targetRotation);
      continue;
    }
    const t = Math.min(1, (now - rp.lerpStart) / rp.lerpDuration);
    rp.mesh.position.lerpVectors(rp.prevPosition, rp.targetPosition, t);
    rp.mesh.rotation.x = lerpAngle(rp.prevRotation.x, rp.targetRotation.x, t);
    rp.mesh.rotation.y = lerpAngle(rp.prevRotation.y, rp.targetRotation.y, t);
    rp.mesh.rotation.z = lerpAngle(rp.prevRotation.z, rp.targetRotation.z, t);
  }
}

function lerpAngle(a: number, b: number, t: number): number {
  // Wrap-around shortest-path lerp on Euler angles.
  const TAU = Math.PI * 2;
  let d = ((b - a) % TAU + TAU) % TAU;
  if (d > Math.PI) d -= TAU;
  return a + d * t;
}

function createAvatarMesh(player: PlayerLike): THREE.Object3D {
  const group = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 1.7, 0.6),
    new THREE.MeshStandardMaterial({ color: parseSafeColor(player.color) }),
  );
  body.position.y = 0.85;
  group.add(body);
  // Eye/nose marker so we can see facing.
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.12, 0.25, 8),
    new THREE.MeshStandardMaterial({ color: 0xffffff }),
  );
  nose.rotation.x = Math.PI / 2;
  nose.position.set(0, 1.4, -0.35);
  group.add(nose);

  if (player.nickname) {
    group.add(makeNameTag(player.nickname));
  }
  return group;
}

function makeNameTag(text: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  const fontSize = 48;
  if (!ctx) return new THREE.Sprite(new THREE.SpriteMaterial());
  ctx.font = `${fontSize}px sans-serif`;
  const w = Math.ceil(ctx.measureText(text).width) + 16;
  const h = fontSize + 12;
  canvas.width = w;
  canvas.height = h;
  ctx.font = `${fontSize}px sans-serif`;
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = "#fff";
  ctx.textBaseline = "top";
  ctx.fillText(text, 8, 6);
  const tex = new THREE.CanvasTexture(canvas);
  tex.needsUpdate = true;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.position.y = 2.0;
  sprite.scale.set(w / 64, h / 64, 1);
  return sprite;
}

function parseSafeColor(value: string): THREE.ColorRepresentation {
  if (!value) return 0xffffff;
  try {
    return new THREE.Color(value);
  } catch {
    return 0xffffff;
  }
}

function disposeMesh(obj: THREE.Object3D): void {
  obj.traverse((node) => {
    if (node instanceof THREE.Mesh) {
      node.geometry?.dispose();
      const m = node.material;
      if (Array.isArray(m)) for (const mm of m) mm.dispose();
      else m?.dispose();
    } else if (node instanceof THREE.Sprite) {
      node.material?.map?.dispose();
      node.material?.dispose();
    }
  });
}

function mapKeys<T>(m: { forEach: (cb: (v: T, k: string) => void) => void }): string[] {
  const out: string[] = [];
  m.forEach((_v, k) => out.push(k));
  return out;
}

function wireChat(input: HTMLInputElement, send: (text: string) => void): void {
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      const text = input.value.trim();
      input.value = "";
      input.blur();
      if (text) send(text);
    }
  });
}

function renderChat(overlay: HTMLDivElement, chat: Iterable<ChatMessageLike>): void {
  const arr = Array.from(chat);
  const recent = arr.slice(-CHAT_LIMIT);
  overlay.innerHTML = "";
  for (const msg of recent) {
    const row = document.createElement("div");
    row.className = "msg";
    const author = document.createElement("span");
    author.className = "author";
    author.textContent = `${msg.authorId}:`;
    const body = document.createElement("span");
    body.textContent = msg.text;
    row.appendChild(author);
    row.appendChild(body);
    overlay.appendChild(row);
  }
}

/**
 * Slug resolution from URL hash (SPEC §wave-3G):
 *   - non-empty + valid           → use as-is
 *   - non-empty + invalid         → log warning, generate a fresh one
 *   - empty                       → generate a fresh `<adj>-<noun>` slug
 *
 * After resolution, the URL hash is updated to the resolved slug so reloads
 * rejoin the same room. We don't push a new history entry — replaceState
 * keeps Back-button behaviour clean.
 */
function resolveRoomId(): string {
  const hashRaw = location.hash.replace(/^#/, "").trim();
  if (hashRaw.length > 0) {
    const v = validateSlug(hashRaw);
    if (v.ok) {
      return v.slug;
    }
    console.warn(
      `[main] invalid slug in URL hash ("${hashRaw}"): ${v.reason}; generating a fresh one`,
    );
  }
  const generated = generateSlug();
  // Mirror the generated slug into the URL so reloading rejoins.
  try {
    history.replaceState(null, "", `#${generated}`);
  } catch (err) {
    console.warn("[main] could not update URL hash", err);
  }
  return generated;
}

function setStatus(text: string): void {
  const el = document.querySelector<HTMLDivElement>("#status");
  if (el) el.textContent = text;
}

function mustGetElement<T extends Element>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`missing #${selector}`);
  return el;
}
